import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import {
  useNavigate,
} from "react-router-dom";

import { db } from "../firebase";

import "./SecondDepositBonusTZS.css";


/* =========================================================
   COLLECTIONS
========================================================= */

const TSH_COLLECTION =
  "tsh_transactions";

const VISITOR_COLLECTION =
  "visterdeposte";

const USERS_COLLECTION =
  "users";


/* =========================================================
   CURRENCY
========================================================= */

const TZS_CURRENCY =
  "TZS";

const TZS_BALANCE_FIELD =
  "tshBalance";


/* =========================================================
   SECOND DEPOSIT BONUS SETTINGS
========================================================= */

const SECOND_DEPOSIT_MINIMUM_TZS =
  300000;


/* =========================================================
   BONUS TIER THRESHOLDS
========================================================= */

const SECOND_BONUS_TIER_1 =
  500000;

const SECOND_BONUS_TIER_2 =
  1000000;

const SECOND_BONUS_TIER_3 =
  3000000;

const SECOND_BONUS_TIER_4 =
  5000000;


/* =========================================================
   COUNTRY → CURRENCY
========================================================= */

const countryCurrency = {

  Tanzania:
    "TZS",

  Kenya:
    "KES",

  Uganda:
    "UGX",

  Rwanda:
    "RWF",

  Burundi:
    "BIF",

  "DRC - Congo":
    "USD",

  Zambia:
    "ZMW",

  Malawi:
    "MWK",

  Mozambique:
    "MZN",

  Zimbabwe:
    "USD",

  "South Sudan":
    "SSP",

  Botswana:
    "BWP",

  Madagascar:
    "MGA",

};


/* =========================================================
   CURRENCY → TZS RATES
========================================================= */

const currencyRates = {

  Kenya:
    21.5,

  Uganda:
    0.77,

  Rwanda:
    1.9,

  Burundi:
    0.94,

  "DRC - Congo":
    2762.43,

  Zambia:
    147.23,

  Malawi:
    1.382,

  Mozambique:
    43.71,

  Zimbabwe:
    2762.43,

  "South Sudan":
    0.469,

  Botswana:
    211.95,

  Madagascar:
    0.684,

};


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (
  amount,
  currency = "TZS"
) => {

  return `${Number(
    amount || 0
  ).toLocaleString()} ${currency}`;

};


/* =========================================================
   NORMALIZE USERNAME
========================================================= */

const normalizeUsername = (
  username
) => {

  return String(
    username || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  return String(
    status || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE POSITION
========================================================= */

const normalizePosition = (
  position
) => {

  return String(
    position || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   GET DEPOSIT POSITION
========================================================= */

const getDepositPosition = (
  deposit
) => {

  return normalizePosition(

    deposit?.position ||
    deposit?.depositOrderType ||
    ""

  );

};


/* =========================================================
   TIMESTAMP
========================================================= */

const getTimestampSeconds = (
  item
) => {

  const value =
    item?.createdAt ||
    item?.timestamp ||
    item?.paidAt ||
    item?.updatedAt;


  if (
    !value
  ) {

    return 0;

  }


  if (
    typeof value?.seconds ===
    "number"
  ) {

    return Number(
      value.seconds
    );

  }


  if (
    typeof value?.toDate ===
    "function"
  ) {

    const date =
      value.toDate();


    if (
      date instanceof Date &&
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return Math.floor(
        date.getTime() /
        1000
      );

    }

  }


  if (
    typeof value ===
    "number"
  ) {

    return value >
      100000000000

      ? Math.floor(
          value / 1000
        )

      : Math.floor(
          value
        );

  }


  const parsed =
    new Date(
      value
    ).getTime();


  if (
    Number.isNaN(
      parsed
    )
  ) {

    return 0;

  }


  return Math.floor(
    parsed /
    1000
  );

};


/* =========================================================
   GET VISITOR CURRENCY
========================================================= */

const getVisitorCurrency = (
  item
) => {

  if (
    item?.currency
  ) {

    return String(
      item.currency
    )
      .trim()
      .toUpperCase();

  }


  if (
    item?.sourceCurrency
  ) {

    return String(
      item.sourceCurrency
    )
      .trim()
      .toUpperCase();

  }


  return (

    countryCurrency[
      item?.country
    ] || ""

  );

};


/* =========================================================
   GET TRANSACTION CURRENCY
========================================================= */

const getTransactionCurrency = (
  item
) => {

  if (
    item?.source ===
    TSH_COLLECTION
  ) {

    return TZS_CURRENCY;

  }


  return getVisitorCurrency(
    item
  );

};


/* =========================================================
   CURRENCY → BALANCE FIELD
========================================================= */

const getBalanceFieldFromCurrency = (
  currency
) => {

  const normalized =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  if (
    normalized ===
    "TZS"
  ) {

    return TZS_BALANCE_FIELD;

  }


  if (
    !normalized
  ) {

    return "";

  }


  return `${normalized}Balance`;

};


/* =========================================================
   GET CURRENCY RATE
========================================================= */

const getCurrencyRate = (
  currency,
  country
) => {

  const normalizedCurrency =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  if (
    normalizedCurrency ===
    "TZS"
  ) {

    return 1;

  }


  if (
    country
  ) {

    const countryRate =
      Number(
        currencyRates[
          country
        ] || 0
      );


    if (
      countryRate > 0
    ) {

      return countryRate;

    }

  }


  const matchingCountry =
    Object.keys(
      countryCurrency
    ).find(
      countryName =>

        String(
          countryCurrency[
            countryName
          ] || ""
        )
          .trim()
          .toUpperCase() ===
        normalizedCurrency

    );


  if (
    matchingCountry
  ) {

    return Number(
      currencyRates[
        matchingCountry
      ] || 0
    );

  }


  return 0;

};


/* =========================================================
   INTERNAL → TZS
========================================================= */

const convertToTZSInternal = (
  amount,
  currency,
  country
) => {

  const numericAmount =
    Number(
      amount || 0
    );


  if (
    numericAmount <=
    0
  ) {

    return 0;

  }


  const normalizedCurrency =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  if (
    normalizedCurrency ===
    "TZS"
  ) {

    return Math.round(
      numericAmount
    );

  }


  const rate =
    getCurrencyRate(
      normalizedCurrency,
      country
    );


  if (
    rate <=
    0
  ) {

    return 0;

  }


  return Math.round(
    numericAmount *
    rate
  );

};


/* =========================================================
   GET SECOND BONUS DATA
========================================================= */

const getSecondBonusData = (
  originalAmount,
  tzsEquivalent
) => {

  const amount =
    Number(
      originalAmount || 0
    );


  const equivalent =
    Number(
      tzsEquivalent || 0
    );


  if (
    amount <=
    0 ||
    equivalent <=
    0
  ) {

    return {

      locked:
        true,

      percent:
        0,

      bonus:
        0,

      label:
        "Bonus unavailable",

    };

  }


  if (
    equivalent <
    SECOND_DEPOSIT_MINIMUM_TZS
  ) {

    return {

      locked:
        true,

      percent:
        0,

      bonus:
        0,

      label:
        "Second Deposit Bonus Locked",

    };

  }


  if (
    equivalent <
    SECOND_BONUS_TIER_1
  ) {

    return {

      locked:
        false,

      percent:
        5,

      bonus:
        Math.round(
          amount *
          0.05
        ),

      label:
        "Second Deposit 5%",

    };

  }


  if (
    equivalent <
    SECOND_BONUS_TIER_2
  ) {

    return {

      locked:
        false,

      percent:
        6,

      bonus:
        Math.round(
          amount *
          0.06
        ),

      label:
        "Second Deposit 6%",

    };

  }


  if (
    equivalent <
    SECOND_BONUS_TIER_3
  ) {

    return {

      locked:
        false,

      percent:
        7,

      bonus:
        Math.round(
          amount *
          0.07
        ),

      label:
        "Second Deposit 7%",

    };

  }


  if (
    equivalent <
    SECOND_BONUS_TIER_4
  ) {

    return {

      locked:
        false,

      percent:
        8,

      bonus:
        Math.round(
          amount *
          0.08
        ),

      label:
        "Second Deposit 8%",

    };

  }


  return {

    locked:
      false,

    percent:
      0,

    bonus:
      500000,

    label:
      "Second Deposit 500,000 Fixed",

  };

};


/* =========================================================
   FIND USER
========================================================= */

const findUserByUsername = async (
  username
) => {

  if (
    !username
  ) {

    return null;

  }


  const usersRef =
    collection(
      db,
      USERS_COLLECTION
    );


  try {

    const exactQuery =
      query(

        usersRef,

        where(
          "username",
          "==",
          username
        )

      );


    const snapshot =
      await getDocs(
        exactQuery
      );


    if (
      !snapshot.empty
    ) {

      return snapshot.docs[0];

    }

  }

  catch (
    error
  ) {

    console.warn(
      "Exact username lookup failed:",
      error
    );

  }


  const allUsers =
    await getDocs(
      usersRef
    );


  const normalized =
    normalizeUsername(
      username
    );


  return (

    allUsers.docs.find(
      userDoc =>

        normalizeUsername(
          userDoc.data()?.username
        ) ===
        normalized

    ) || null

  );

};


/* =========================================================
   NORMALIZE TSH DEPOSIT
========================================================= */

const normalizeTshDeposit = (
  document
) => {

  const data =
    document.data();


  const amount =
    Number(
      data.amount || 0
    );


  return {

    id:
      document.id,

    ...data,

    source:
      TSH_COLLECTION,

    sourceCurrency:
      TZS_CURRENCY,

    originalAmount:
      amount,

  };

};


/* =========================================================
   NORMALIZE VISITOR DEPOSIT
========================================================= */

const normalizeVisitorDeposit = (
  document
) => {

  const data =
    document.data();


  const amount =
    Number(
      data.amount || 0
    );


  const currency =
    getVisitorCurrency(
      data
    );


  return {

    id:
      document.id,

    ...data,

    source:
      VISITOR_COLLECTION,

    sourceCurrency:
      currency,

    originalAmount:
      amount,

  };

};


/* =========================================================
   GET SUCCESSFUL DEPOSITS
========================================================= */

const getSuccessfulDeposits =
  async (
    username
  ) => {

    const tshQuery =
      query(

        collection(
          db,
          TSH_COLLECTION
        ),

        where(
          "username",
          "==",
          username
        ),

        where(
          "status",
          "==",
          "success"
        )

      );


    const visitorQuery =
      query(

        collection(
          db,
          VISITOR_COLLECTION
        ),

        where(
          "username",
          "==",
          username
        ),

        where(
          "status",
          "==",
          "success"
        )

      );


    const [
      tshSnapshot,
      visitorSnapshot,
    ] = await Promise.all([

      getDocs(
        tshQuery
      ),

      getDocs(
        visitorQuery
      ),

    ]);


    const tshDeposits =
      tshSnapshot.docs.map(
        normalizeTshDeposit
      );


    const visitorDeposits =
      visitorSnapshot.docs.map(
        normalizeVisitorDeposit
      );


    const allDeposits = [

      ...tshDeposits,

      ...visitorDeposits,

    ];


    const validDeposits =
      allDeposits.filter(
        item => {

          return (

            normalizeUsername(
              item.username
            ) ===
            normalizeUsername(
              username
            ) &&

            normalizeStatus(
              item.status
            ) ===
            "success" &&

            Number(
              item.originalAmount ||
              0
            ) > 0

          );

        }
      );


    validDeposits.sort(
      (
        a,
        b
      ) => {

        const timeA =
          getTimestampSeconds(
            a
          );


        const timeB =
          getTimestampSeconds(
            b
          );


        if (
          timeA ===
          timeB
        ) {

          return String(
            a.id
          ).localeCompare(
            String(
              b.id
            )
          );

        }


        return (
          timeA -
          timeB
        );

      }
    );


    return validDeposits;

  };


/* =========================================================
   GET SECOND DEPOSIT
========================================================= */

const getSecondDeposit =
  (
    deposits
  ) => {

    if (
      !Array.isArray(
        deposits
      ) ||
      deposits.length ===
      0
    ) {

      return null;

    }


    /*
      Explicit second first.
    */

    const explicitSecond =
      deposits.find(
        deposit => {

          return (
            getDepositPosition(
              deposit
            ) ===
            "second"
          );

        }
      );


    if (
      explicitSecond
    ) {

      return explicitSecond;

    }


    /*
      Legacy deposits only.
    */

    const legacyDeposits =
      deposits.filter(
        deposit => {

          return (
            getDepositPosition(
              deposit
            ) ===
            ""
          );

        }
      );


    if (
      legacyDeposits.length >=
      2
    ) {

      return legacyDeposits[1];

    }


    return null;

  };


/* =========================================================
   COMPONENT
========================================================= */

export default function SecondDepositBonusTZS() {

  const navigate =
    useNavigate();


  const [
    username,
    setUsername,
  ] = useState("");


  const [
    deposit,
    setDeposit,
  ] = useState(null);


  const [
    bonus,
    setBonus,
  ] = useState(0);


  const [
    bonusData,
    setBonusData,
  ] = useState(null);


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    applying,
    setApplying,
  ] = useState(false);


  const [
    completed,
    setCompleted,
  ] = useState(false);


  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     LOAD SECOND DEPOSIT BONUS
  ======================================================= */

  useEffect(
    () => {

      const loadBonus =
        async () => {

          try {

            setLoading(
              true
            );


            const storedUsername =
              localStorage.getItem(
                "username"
              );


            if (
              !storedUsername
            ) {

              setMessage(
                "Login required."
              );

              return;

            }


            setUsername(
              storedUsername
            );


            const userDoc =
              await findUserByUsername(
                storedUsername
              );


            if (
              !userDoc
            ) {

              setMessage(
                "Account not found."
              );

              return;

            }


            const userData =
              userDoc.data();


            if (
              userData.secondDepositBonusClaimed ===
              true
            ) {

              setCompleted(
                true
              );

              setMessage(
                "Second Deposit Bonus tayari imetolewa."
              );

              return;

            }


            if (
              userData.secondDepositBonusTZSClaimed ===
              true
            ) {

              setCompleted(
                true
              );

              setMessage(
                "Second Deposit Bonus tayari imetolewa."
              );

              return;

            }


            const deposits =
              await getSuccessfulDeposits(
                storedUsername
              );


            const secondDeposit =
              getSecondDeposit(
                deposits
              );


            if (
              !secondDeposit
            ) {

              setMessage(
                "Second deposit bado haijapatikana."
              );

              return;

            }


            const position =
              getDepositPosition(
                secondDeposit
              );


            if (
              position &&
              position !==
              "second"
            ) {

              setMessage(
                "Second deposit haijapatikana."
              );

              return;

            }


            if (
              secondDeposit.selfBonusGiven ===
              true
            ) {

              setCompleted(
                true
              );

              setMessage(
                "Second Deposit Bonus tayari imetolewa."
              );

              return;

            }


            if (
              secondDeposit.adminBonusGiven ===
              true
            ) {

              setCompleted(
                true
              );

              setMessage(
                "Second Deposit Bonus tayari imelipwa na Admin."
              );

              return;

            }


            const originalAmount =
              Number(
                secondDeposit.originalAmount ||
                secondDeposit.amount ||
                0
              );


            if (
              originalAmount <=
              0
            ) {

              setMessage(
                "Second deposit amount haijapatikana."
              );

              return;

            }


            const currency =
              getTransactionCurrency(
                secondDeposit
              );


            if (
              !currency
            ) {

              setMessage(
                "Deposit currency haijulikani."
              );

              return;

            }


            const tzsEquivalent =
              convertToTZSInternal(
                originalAmount,
                currency,
                secondDeposit.country
              );


            if (
              tzsEquivalent <=
              0
            ) {

              setMessage(
                "Unable to verify deposit eligibility."
              );

              return;

            }


            const calculated =
              getSecondBonusData(
                originalAmount,
                tzsEquivalent
              );


            if (
              calculated.locked
            ) {

              setDeposit(
                secondDeposit
              );

              setBonusData(
                calculated
              );

              setBonus(
                0
              );

              setMessage(
                "Second deposit yako haijafikia kiwango cha chini cha Bonus."
              );

              return;

            }


            setDeposit(
              secondDeposit
            );

            setBonusData(
              calculated
            );

            setBonus(
              calculated.bonus
            );

            setMessage(
              ""
            );

          }

          catch (
            error
          ) {

            console.error(
              "Second Deposit Bonus loading error:",
              error
            );

            setMessage(
              "Unable to load Second Deposit Bonus."
            );

          }

          finally {

            setLoading(
              false
            );

          }

        };


      loadBonus();

    },
    []
  );


  /* =======================================================
     APPLY BONUS
  ======================================================= */

  const applyBonus =
    async () => {

      if (
        !deposit ||
        !username ||
        !bonus ||
        applying ||
        completed
      ) {

        return;

      }


      setApplying(
        true
      );

      setMessage(
        ""
      );


      try {

        const userDoc =
          await findUserByUsername(
            username
          );


        if (
          !userDoc
        ) {

          throw new Error(
            "User account not found."
          );

        }


        const depositRef =
          doc(
            db,
            deposit.source,
            deposit.id
          );


        let finalBonus =
          0;

        let finalCurrency =
          "";

        let finalAmount =
          0;

        let finalPercent =
          0;

        let finalLabel =
          "";

        let finalBalanceField =
          "";

        let finalTZSEquivalent =
          0;


        await runTransaction(
          db,
          async (
            transaction
          ) => {

            const depositSnapshot =
              await transaction.get(
                depositRef
              );


            if (
              !depositSnapshot.exists()
            ) {

              throw new Error(
                "Second deposit not found."
              );

            }


            const freshDeposit =
              depositSnapshot.data();


            if (
              normalizeStatus(
                freshDeposit.status
              ) !==
              "success"
            ) {

              throw new Error(
                "Deposit is not successful."
              );

            }


            const freshPosition =
              getDepositPosition(
                freshDeposit
              );


            if (
              freshPosition &&
              freshPosition !==
              "second"
            ) {

              throw new Error(
                "Deposit hii si Second Deposit."
              );

            }


            if (
              freshDeposit.selfBonusGiven ===
              true
            ) {

              throw new Error(
                "Second Deposit Bonus tayari imetolewa."
              );

            }


            if (
              freshDeposit.adminBonusGiven ===
              true
            ) {

              throw new Error(
                "Bonus hii tayari imelipwa na Admin."
              );

            }


            const userSnapshot =
              await transaction.get(
                userDoc.ref
              );


            if (
              !userSnapshot.exists()
            ) {

              throw new Error(
                "User account not found."
              );

            }


            const userData =
              userSnapshot.data();


            if (
              userData.secondDepositBonusClaimed ===
              true
            ) {

              throw new Error(
                "Second Deposit Bonus tayari imetolewa."
              );

            }


            if (
              userData.secondDepositBonusTZSClaimed ===
              true
            ) {

              throw new Error(
                "Second Deposit Bonus tayari imetolewa."
              );

            }


            finalAmount =
              Number(
                freshDeposit.amount ||
                0
              );


            if (
              finalAmount <=
              0
            ) {

              throw new Error(
                "Deposit amount haijapatikana."
              );

            }


            finalCurrency =
              getTransactionCurrency({

                ...freshDeposit,

                source:
                  deposit.source,

              });


            if (
              !finalCurrency
            ) {

              throw new Error(
                "Deposit currency haijulikani."
              );

            }


            finalBalanceField =
              getBalanceFieldFromCurrency(
                finalCurrency
              );


            if (
              !finalBalanceField
            ) {

              throw new Error(
                `Balance field haijapatikana kwa ${finalCurrency}.`
              );

            }


            finalTZSEquivalent =
              convertToTZSInternal(
                finalAmount,
                finalCurrency,
                freshDeposit.country
              );


            if (
              finalTZSEquivalent <=
              0
            ) {

              throw new Error(
                "Unable to verify deposit eligibility."
              );

            }


            const calculatedBonus =
              getSecondBonusData(
                finalAmount,
                finalTZSEquivalent
              );


            if (
              calculatedBonus.locked
            ) {

              throw new Error(
                "Second deposit haijafikia kiwango cha Bonus."
              );

            }


            finalBonus =
              Number(
                calculatedBonus.bonus ||
                0
              );


            finalPercent =
              Number(
                calculatedBonus.percent ||
                0
              );


            finalLabel =
              calculatedBonus.label ||
              "Second Deposit Bonus";


            if (
              finalBonus <=
              0
            ) {

              throw new Error(
                "Second Deposit Bonus amount is invalid."
              );

            }


            const currentBalance =
              Number(
                userData[
                  finalBalanceField
                ] || 0
              );


            const newBalance =
              currentBalance +
              finalBonus;


            transaction.update(
              userDoc.ref,
              {

                [finalBalanceField]:
                  newBalance,

                secondDepositBonusClaimed:
                  true,

              }
            );


            transaction.update(
              depositRef,
              {

                selfBonusGiven:
                  true,

                adminBonusGiven:
                  false,

                selfBonusAmount:
                  finalBonus,

                selfBonusClaimedAmount:
                  finalBonus,

                selfBonusCurrency:
                  finalCurrency,

                bonusCurrency:
                  finalCurrency,

                selfBonusPercent:
                  finalPercent,

                selfBonusLabel:
                  finalLabel,

                selfBonusType:
                  "second",

                selfBonusLocked:
                  false,

                selfBonusApprovedBy:
                  "user",

                selfBonusApprovedAt:
                  serverTimestamp(),

                bonusDepositAmount:
                  finalAmount,

                bonusDepositCurrency:
                  finalCurrency,

                bonusDepositTZSEquivalent:
                  finalTZSEquivalent,

                bonusBalanceField:
                  finalBalanceField,

                bonusStatus:
                  "credited",

                bonusTransactionType:
                  "depositBonus",

                depositOrderType:
                  "second",

                depositPosition:
                  2,

                bonusRule:
                  finalLabel,

                bonusRulePercent:
                  finalPercent,

              }
            );

          }
        );


        setCompleted(
          true
        );


        setBonus(
          finalBonus
        );


        setBonusData({

          locked:
            false,

          percent:
            finalPercent,

          bonus:
            finalBonus,

          label:
            finalLabel,

        });


        setMessage(
          `Hongera! ${formatMoney(
            finalBonus,
            finalCurrency
          )} imeongezwa kwenye balance yako.`
        );

      }

      catch (
        error
      ) {

        console.error(
          "Second Deposit Bonus payment failed:",
          error
        );


        setMessage(
          error?.message ||
          "Second Deposit Bonus payment failed."
        );

      }

      finally {

        setApplying(
          false
        );

      }

    };


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="sd-page">

        <button
          type="button"
          className="sd-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >

          <span className="sd-back-icon">
            ←
          </span>

          <span>
            Back
          </span>

        </button>


        <div
          className="
            sd-card
            sd-loading
          "
        >

          <div className="sd-loader" />

          <p>
            Checking your second deposit...
          </p>

        </div>

      </div>

    );

  }


  /* =======================================================
     CURRENT CURRENCY
  ======================================================= */

  const currentCurrency =
    deposit
      ? getTransactionCurrency(
          deposit
        )
      : TZS_CURRENCY;


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="sd-page">


      {/* =================================================
          BACK BUTTON
      ================================================= */}

      <button
        type="button"
        className="sd-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >

        <span className="sd-back-icon">
          ←
        </span>

        <span>
          Back
        </span>

      </button>


      {/* =================================================
          MAIN CARD
      ================================================= */}

      <div className="sd-card">


        {/* =================================================
            GIFT
        ================================================= */}

        <div className="sd-gift">
          🎁
        </div>


        {/* =================================================
            BADGE
        ================================================= */}

        <div className="sd-badge">
          SECOND DEPOSIT
        </div>


        {/* =================================================
            TITLE
        ================================================= */}

        <h2>
          Second Deposit Bonus
        </h2>


        {/* =================================================
            SUBTITLE
        ================================================= */}

        <p className="sd-subtitle">

          Hongera{" "}

          <strong>
            {username || "Trader"}
          </strong>

          !

          <br />

          Deposit yako ya pili
          imepatikana.

        </p>


        {/* =================================================
            DEPOSIT BOX
        ================================================= */}

        {deposit && (

          <div className="sd-deposit-box">


            <div>

              <span>
                Second Deposit
              </span>

              <strong>

                {formatMoney(
                  deposit.originalAmount ||
                  deposit.amount ||
                  0,
                  currentCurrency
                )}

              </strong>

            </div>


            <div>

              <span>
                Source
              </span>

              <strong>

                {
                  deposit.source ===
                  VISITOR_COLLECTION

                    ? "Visitor Deposit"

                    : "TZS Deposit"
                }

              </strong>

            </div>


            {deposit.country && (

              <div>

                <span>
                  Country
                </span>

                <strong>
                  {deposit.country}
                </strong>

              </div>

            )}


            <div>

              <span>
                Currency
              </span>

              <strong>
                {currentCurrency}
              </strong>

            </div>


            {bonusData &&
            !bonusData.locked && (

              <div>

                <span>
                  Bonus Rule
                </span>

                <strong>
                  {bonusData.label}
                </strong>

              </div>

            )}

          </div>

        )}


        {/* =================================================
            BONUS
        ================================================= */}

        {bonus > 0 && (

          <div className="sd-bonus-box">

            <span>
              YOUR BONUS
            </span>

            <strong>

              {formatMoney(
                bonus,
                currentCurrency
              )}

            </strong>

            <small>

              {bonusData?.percent > 0

                ? `${bonusData.percent}% Second Deposit Bonus`

                : "Fixed Second Deposit Bonus"}

            </small>

          </div>

        )}


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (

          <div
            className={`
              sd-message
              ${
                completed
                  ? "sd-success"
                  : "sd-info"
              }
            `}
          >

            <span>

              {
                completed
                  ? "✓"
                  : "!"
              }

            </span>

            <p>
              {message}
            </p>

          </div>

        )}


        {/* =================================================
            APPLY
        ================================================= */}

        {bonus > 0 &&
        !completed && (

          <button
            type="button"
            className="sd-apply-btn"
            onClick={applyBonus}
            disabled={applying}
          >

            {applying ? (

              <>

                <span
                  className="sd-button-loader"
                />

                Processing...

              </>

            ) : (

              <>

                APPLY BONUS NOW

                <span>
                  →
                </span>

              </>

            )}

          </button>

        )}


        {/* =================================================
            PAID
        ================================================= */}

        {completed && (

          <div className="sd-paid">

            <span>
              ✓
            </span>

            Bonus Already Claimed

          </div>

        )}


        {/* =================================================
            NOTE
        ================================================= */}

        <p className="sd-note">

          Bonus itaongezwa kwenye{" "}

          <strong>

            {
              currentCurrency ===
              "TZS"

                ? "TZS balance"

                : `${currentCurrency} balance`
            }

          </strong>

          {" "}yako baada ya
          kubonyeza Apply Bonus Now.

        </p>


      </div>

    </div>

  );

}
